import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { getFormValuesFromLocation } from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/FundingInformation/FundingInformation.utils';
import FeeProviderLocationTable, {
  LocationTableWrapper,
} from 'app/shared/modules/FeeProviderLocationTable';
import _ from 'lodash';

import { statesWithNationalCategoryOptions } from 'constants/options';

import { closeDialogAction, openDialogAction } from 'store/global/actions';

import { WireToInformationFieldId } from '../../Content.constants';

import { ADVANCE_AMOUNT_FIELDS, CLEARED_LOCATION_FIELDS } from './constants';
import { getAdvanceAmountPercentUpdate } from './utils';

export const useFundingHandlers = ({
  formRef,
  setFormRef,
  setIsTouchedRef,
  validateFieldRef,
  isSaving,
  setIsLoading,
  feeProviderOptions,
}) => {
  const dispatch = useDispatch();
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;

  const onChooseLocation = useCallback(
    (__, { location }) => {
      dispatch(closeDialogAction());

      const { closingAgentFeeProviderFullAddress, ...locationFields } =
        getFormValuesFromLocation(location);

      setIsTouchedRef.current(true);
      setFormRef.current(prev => ({
        ...prev,
        ...locationFields,
        [WireToInformationFieldId.FEE_PROVIDER_ADDRESS]: closingAgentFeeProviderFullAddress,
        [WireToInformationFieldId.STATE]: statesWithNationalCategoryOptions.find(
          i => i.value === locationFields.state,
        ),
      }));
    },
    [dispatch, setFormRef, setIsTouchedRef],
  );

  const onOpenFeeProviderLocationTable = useCallback(
    option =>
      dispatch(
        openDialogAction({
          header: 'Choose location',
          size: ModalSize.FULL,
          body: dialogProps => (
            <LocationTableWrapper>
              <FeeProviderLocationTable
                {...dialogProps}
                feeProviderId={
                  option?.id || formRef.current[WireToInformationFieldId.FEE_PROVIDER_ID]?.value
                }
                isLoading={isSavingRef.current}
                setIsLoading={setIsLoading}
                onChangeLocationCallback={onOpenFeeProviderLocationTable}
                onLocationRowCustomClick={onChooseLocation}
              />
            </LocationTableWrapper>
          ),
        }),
      ),
    [dispatch, setIsLoading, onChooseLocation, formRef],
  );

  const onFeeProviderChange = useCallback(
    (...props) => {
      const option = props[1] || {};

      setIsTouchedRef.current(true);
      setFormRef.current(prev => ({
        ...prev,
        [WireToInformationFieldId.FEE_PROVIDER_ID]: option,
        [WireToInformationFieldId.COMPANY_NAME]: option.name || '',
        ...CLEARED_LOCATION_FIELDS,
      }));

      if (!_.size(option)) {
        return;
      }

      validateFieldRef.current?.(WireToInformationFieldId.FEE_PROVIDER_ID, option);

      if (option.locations?.length === 1) {
        return onChooseLocation(null, { location: option.locations[0] });
      }

      onOpenFeeProviderLocationTable(option);
    },
    [
      onChooseLocation,
      onOpenFeeProviderLocationTable,
      setFormRef,
      setIsTouchedRef,
      validateFieldRef,
    ],
  );

  const onChangeAdvanceAmountPercentDependantField = useCallback(
    e => {
      const { value, name } = e.target;

      if (!ADVANCE_AMOUNT_FIELDS.has(name)) {
        return;
      }

      setIsTouchedRef.current(true);

      const update = getAdvanceAmountPercentUpdate({ name, value, formRef });
      setFormRef.current(prevValues => ({ ...prevValues, ...update }));
      validateFieldRef.current?.(name, value, formRef.current);
    },
    [formRef, setFormRef, setIsTouchedRef, validateFieldRef],
  );

  const selectedFeeProviderId = formRef.current?.[WireToInformationFieldId.FEE_PROVIDER_ID]?.value;
  const selectedFeeProvider = feeProviderOptions?.find(p => p.value === selectedFeeProviderId);
  const isChangeLocationButtonDisabled = isSaving || !selectedFeeProviderId;
  const isChangeLocationButtonVisible = selectedFeeProvider?.locations?.length > 1;

  return {
    onFeeProviderChange,
    onOpenFeeProviderLocationTable,
    onChangeAdvanceAmountPercentDependantField,
    isChangeLocationButtonDisabled,
    isChangeLocationButtonVisible,
  };
};
